import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Download,
  Headphones,
  ImagePlus,
  MessageSquare,
  Phone,
  Wrench,
  X,
} from 'lucide-react';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { fundingAuthAPI } from '@/utils/api';
import { setRuntimeModuleCredits } from '@/constants/ipeCredits';
import { IPE_MANUAL_HOME_PATH, IPE_PLUGIN_INSTALL_PATH, openUrlInNewTab } from '@/config/ipeRoutes';
import { resolveNavFromWorkbenchLocation } from '@/pages/ipe/workbench/constants/workbenchNavConfig';
import { IPE_SUPPORT_CONFIG } from '@/config/ipeSupportConfig';
import { isSheinPlatformPath } from '@/shein-platform/constants/sheinRoutes';
import { isTemuPlatformPath } from '@/temu-platform/constants/temuRoutes';
import { isTiktokPlatformPath } from '@/tiktok-platform/constants/tiktokRoutes';
import '@/styles/ipe/ipe-auth.css';
import '@/styles/ipe/ipe-global-float-balls.css';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

const FEEDBACK_MAX_IMAGES = 6;
const FEEDBACK_MAX_FILE_SIZE = 5 * 1024 * 1024;
const FEEDBACK_IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp';

function readFeedbackFileExtension(fileName) {
  const base = String(fileName || '').trim();
  const idx = base.lastIndexOf('.');
  if (idx < 0 || idx === base.length - 1) return '';
  return base.slice(idx + 1).toLowerCase();
}

/** @returns {string|null} i18n key under globalFloat, or null if ok */
function validateFeedbackImageFile(file) {
  if (!file) return 'errInvalidFile';
  const mime = String(file.type || '').trim().toLowerCase();
  const ext = readFeedbackFileExtension(file.name);
  const allowedMime = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const allowedExt = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  if (mime && !allowedMime.includes(mime)) {
    return 'errImageType';
  }
  if (ext && !allowedExt.includes(ext)) {
    return 'errImageType';
  }
  if (!mime && !ext) {
    return 'errImageType';
  }
  if (file.size > FEEDBACK_MAX_FILE_SIZE) {
    return 'errImageSize';
  }
  return null;
}

function revokeFeedbackImagePreviews(images) {
  images.forEach((item) => {
    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
  });
}

function FloatAction({
  id,
  label,
  icon,
  active = false,
  onToggle,
  onOpen,
  onClose,
  children,
  panelClassName = '',
  panelId,
}) {
  const hasPanel = Boolean(children);

  return (
    <div
      className={`ipe-float-balls__item${active ? ' ipe-float-balls__item--open' : ''}`}
      onMouseEnter={() => {
        if (hasPanel) onOpen?.();
        else onClose?.();
      }}
      onFocus={() => {
        if (hasPanel) onOpen?.();
        else onClose?.();
      }}
    >
      <button
        type="button"
        className={`ipe-float-balls__btn${active ? ' ipe-float-balls__btn--active' : ''}`}
        aria-label={label}
        aria-expanded={hasPanel ? active : undefined}
        aria-controls={hasPanel && active ? panelId : undefined}
        data-float-action={id}
        onClick={hasPanel ? undefined : onToggle}
      >
        <span className="ipe-float-balls__btn-icon" aria-hidden="true">{icon}</span>
        <span className="ipe-float-balls__btn-label">{label}</span>
      </button>
      {hasPanel ? (
        <div
          id={panelId}
          className={`ipe-float-balls__panel ${panelClassName}`.trim()}
          role="region"
          aria-label={label}
          hidden={!active}
        >
          <div className="ipe-float-balls__panel-surface">{children}</div>
        </div>
      ) : null}
    </div>
  );
}

function SimpleModal({
  open,
  title,
  onClose,
  children,
  footer,
  panelClassName = '',
  closeLabel,
}) {
  if (!open) return null;

  return createPortal(
    <div className="ipe-account-modal" role="presentation" onClick={onClose}>
      <div
        className={`ipe-account-modal__panel ${panelClassName}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ipe-float-balls-modal-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <button type="button" className="ipe-account-modal__close" onClick={onClose} aria-label={closeLabel}>
          <X size={18} />
        </button>
        <h2 id="ipe-float-balls-modal-title" className="ipe-account-modal__title">
          {title}
        </h2>
        {children}
        {footer ? <div className="ipe-account-modal__actions">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

function resetFeedbackForm(setters) {
  revokeFeedbackImagePreviews(setters.feedbackImages || []);
  setters.setFeedbackEmail('');
  setters.setFeedbackText('');
  setters.setFeedbackImages([]);
  setters.setFeedbackError('');
  setters.setFeedbackSubmitting(false);
  setters.setFeedbackSent(false);
}

export default function IpeGlobalFloatBalls() {
  const location = useLocation();
  const pathname = location.pathname || '';
  // 平台工作区与管理后台不展示全局悬浮工具栏
  if (
    pathname.startsWith('/ipe/admin')
    || isSheinPlatformPath(pathname)
    || isTemuPlatformPath(pathname)
    || isTiktokPlatformPath(pathname)
  ) {
    return null;
  }
  return <IpeGlobalFloatBallsContent location={location} />;
}

function IpeGlobalFloatBallsContent({ location }) {
  const { t } = useTranslation('workbench');
  const { isAuthenticated, sessionVerified } = useIpeAuth();
  const [supportPhone, setSupportPhone] = useState(IPE_SUPPORT_CONFIG.defaultPhone);
  const [qrcodeError, setQrcodeError] = useState(false);
  const [openPanel, setOpenPanel] = useState(null);
  const [panelSwitching, setPanelSwitching] = useState(false);
  const openPanelRef = useRef(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackImages, setFeedbackImages] = useState([]);
  const feedbackFileInputRef = useRef(null);
  const feedbackImagesRef = useRef(feedbackImages);
  feedbackImagesRef.current = feedbackImages;

  openPanelRef.current = openPanel;

  const tf = useCallback((key, opts) => t(`globalFloat.${key}`, opts), [t]);

  const hideTutorialEntry = useMemo(() => {
    if (location.pathname.includes('/video-generate-detail')) {
      return true;
    }
    const navId = resolveNavFromWorkbenchLocation({
      pathname: location.pathname,
      search: location.search,
    });
    return navId === 'videos';
  }, [location.pathname, location.search]);

  const openPanelById = useCallback((panelId) => {
    if (openPanelRef.current && openPanelRef.current !== panelId) {
      setPanelSwitching(true);
    }
    setOpenPanel(panelId);
  }, []);

  const closePanel = useCallback(() => {
    setPanelSwitching(false);
    setOpenPanel(null);
  }, []);

  useEffect(() => {
    if (!panelSwitching) return undefined;
    const timer = window.setTimeout(() => setPanelSwitching(false), 220);
    return () => window.clearTimeout(timer);
  }, [panelSwitching, openPanel]);

  useEffect(() => {
    if (!openPanel) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closePanel();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closePanel, openPanel]);

  useEffect(() => {
    closePanel();
  }, [closePanel, location.pathname, location.search]);

  useEffect(() => {
    if (!isAuthenticated || !sessionVerified) return undefined;

    let cancelled = false;
    const loadCenter = async () => {
      try {
        const data = await fundingAuthAPI.getAccountCenter();
        if (!cancelled && data?.moduleCredits?.length) {
          setRuntimeModuleCredits(data.moduleCredits);
        }
        const phone = data?.recharge?.phone;
        if (!cancelled && phone) {
          setSupportPhone(String(phone).trim());
        }
      } catch {
        // 未登录或接口不可用时保留默认配置
      }
    };

    const scheduleLoad = () => {
      if (cancelled) return;
      void loadCenter();
    };

    let idleId = null;
    let timeoutId = null;
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(scheduleLoad, { timeout: 2500 });
    } else {
      timeoutId = window.setTimeout(scheduleLoad, 400);
    }

    const onWorkspaceRefresh = () => {
      loadCenter();
    };
    window.addEventListener('ipe:workspace-refresh', onWorkspaceRefresh);

    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
      window.removeEventListener('ipe:workspace-refresh', onWorkspaceRefresh);
    };
  }, [isAuthenticated, sessionVerified]);

  useEffect(() => () => {
    revokeFeedbackImagePreviews(feedbackImagesRef.current);
  }, []);

  const closeFeedbackModal = useCallback(() => {
    setFeedbackOpen(false);
    resetFeedbackForm({
      feedbackImages,
      setFeedbackEmail,
      setFeedbackText,
      setFeedbackImages,
      setFeedbackError,
      setFeedbackSubmitting,
      setFeedbackSent,
    });
  }, [feedbackImages]);

  const handleFeedbackImageSelect = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setFeedbackImages((current) => {
      const remaining = FEEDBACK_MAX_IMAGES - current.length;
      if (remaining <= 0) {
        setFeedbackError(tf('errMaxImages', { max: FEEDBACK_MAX_IMAGES }));
        return current;
      }

      const next = [...current];
      let errorMessage = '';

      files.slice(0, remaining).forEach((file) => {
        const validationKey = validateFeedbackImageFile(file);
        if (validationKey) {
          errorMessage = tf(validationKey);
          return;
        }
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      });

      if (files.length > remaining) {
        errorMessage = tf('errMaxImages', { max: FEEDBACK_MAX_IMAGES });
      }

      setFeedbackError(errorMessage);
      return next;
    });
  }, [tf]);

  const removeFeedbackImage = useCallback((imageId) => {
    setFeedbackImages((current) => {
      const target = current.find((item) => item.id === imageId);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return current.filter((item) => item.id !== imageId);
    });
    setFeedbackError('');
  }, []);

  const openTutorial = useCallback(() => {
    closePanel();
    if (IPE_SUPPORT_CONFIG.tutorialUrl) {
      openUrlInNewTab(IPE_SUPPORT_CONFIG.tutorialUrl);
      return;
    }
    openUrlInNewTab(`${window.location.origin}${IPE_MANUAL_HOME_PATH}`);
  }, [closePanel]);

  const submitFeedback = useCallback(async () => {
    if (!isAuthenticated) {
      setFeedbackError(tf('errLoginRequired'));
      return;
    }

    const email = feedbackEmail.trim();
    const text = feedbackText.trim();

    if (!email) {
      setFeedbackError(tf('errEmailRequired'));
      return;
    }
    if (!isValidEmail(email)) {
      setFeedbackError(tf('errEmailInvalid'));
      return;
    }
    if (!text) {
      setFeedbackError(tf('errContentRequired'));
      return;
    }
    if (text.length < 5) {
      setFeedbackError(tf('errContentMin'));
      return;
    }

    setFeedbackError('');
    setFeedbackSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('content', text);
      if (typeof window !== 'undefined') {
        formData.append('pageUrl', window.location.href);
      }
      feedbackImages.forEach((item) => {
        formData.append('images', item.file);
      });
      await fundingAuthAPI.submitFeedback(formData);
      setFeedbackSent(true);
      revokeFeedbackImagePreviews(feedbackImages);
      setFeedbackImages([]);
      setFeedbackText('');
      setTimeout(() => {
        closeFeedbackModal();
      }, 1400);
    } catch (error) {
      setFeedbackError(error?.message || tf('errSubmit'));
    } finally {
      setFeedbackSubmitting(false);
    }
  }, [closeFeedbackModal, feedbackEmail, feedbackImages, feedbackText, isAuthenticated, tf]);

  const handlePluginDownload = useCallback(() => {
    closePanel();
    openUrlInNewTab(`${window.location.origin}${IPE_PLUGIN_INSTALL_PATH}`);
  }, [closePanel]);

  const openFeedback = useCallback(() => {
    closePanel();
    setFeedbackOpen(true);
  }, [closePanel]);

  return createPortal(
    <>
      <div className="ipe-float-balls" aria-label={tf('aria')}>
        <div
          className={`ipe-float-balls__rail${panelSwitching ? ' ipe-float-balls__rail--switching' : ''}`}
          onMouseLeave={closePanel}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              closePanel();
            }
          }}
        >
          <FloatAction
            id="tools"
            label={tf('tools')}
            icon={<Wrench size={18} strokeWidth={1.75} />}
            active={openPanel === 'tools'}
            onOpen={() => openPanelById('tools')}
            onClose={closePanel}
            panelId="ipe-float-panel-tools"
          >
            <p className="ipe-float-balls__panel-title">{tf('toolsPanel')}</p>
            <div className="ipe-float-balls__menu" role="menu" aria-label={tf('toolsMenuAria')}>
              <button
                type="button"
                className="ipe-float-balls__menu-btn"
                role="menuitem"
                onClick={openFeedback}
              >
                <MessageSquare size={16} strokeWidth={1.8} aria-hidden="true" />
                <span>
                  <strong>{tf('feedback')}</strong>
                  <em>{tf('feedbackDesc')}</em>
                </span>
              </button>
              <button
                type="button"
                className="ipe-float-balls__menu-btn"
                role="menuitem"
                onClick={handlePluginDownload}
              >
                <Download size={16} strokeWidth={1.8} aria-hidden="true" />
                <span>
                  <strong>{tf('installPlugin')}</strong>
                  <em>{tf('installPluginDesc')}</em>
                </span>
              </button>
            </div>
          </FloatAction>

          {!hideTutorialEntry ? (
            <FloatAction
              id="tutorial"
              label={tf('tutorial')}
              icon={<BookOpen size={18} strokeWidth={1.75} />}
              onToggle={openTutorial}
              onClose={closePanel}
            />
          ) : null}

          <FloatAction
            id="support"
            label={tf('support')}
            icon={<Headphones size={18} strokeWidth={1.75} />}
            active={openPanel === 'support'}
            onOpen={() => openPanelById('support')}
            onClose={closePanel}
            panelClassName="ipe-float-balls__panel--service"
            panelId="ipe-float-panel-support"
          >
            <p className="ipe-float-balls__panel-title">{tf('contactSupport')}</p>
            <div className="ipe-float-balls__qrcode-wrap">
              {!qrcodeError ? (
                <img
                  src={IPE_SUPPORT_CONFIG.wechatQrcodeSrc}
                  alt={tf('qrAlt')}
                  className="ipe-float-balls__qrcode"
                  onError={() => setQrcodeError(true)}
                />
              ) : (
                <div className="ipe-float-balls__qrcode-placeholder">
                  <Headphones size={28} strokeWidth={1.6} aria-hidden="true" />
                  <span>{tf('qrMissing')}</span>
                </div>
              )}
            </div>
            <p className="ipe-float-balls__qrcode-hint">{tf('qrHint')}</p>
            <p className="ipe-float-balls__phone">
              <Phone size={14} strokeWidth={1.8} aria-hidden="true" />
              {supportPhone ? (
                <>
                  <span>{tf('phone')}</span>
                  <a href={`tel:${supportPhone}`}>{supportPhone}</a>
                </>
              ) : (
                <span>{tf('phoneMissing')}</span>
              )}
            </p>
          </FloatAction>
        </div>
      </div>

      <SimpleModal
        open={feedbackOpen}
        title={tf('feedback')}
        closeLabel={tf('close')}
        panelClassName="ipe-account-modal__panel--wide ipe-float-balls__feedback-panel"
        onClose={closeFeedbackModal}
        footer={
          feedbackSent ? null : (
            <>
              <button
                type="button"
                className="ipe-account-modal__btn ipe-account-modal__btn--ghost"
                onClick={closeFeedbackModal}
                disabled={feedbackSubmitting}
              >
                {tf('cancel')}
              </button>
              <button
                type="button"
                className="ipe-account-modal__btn ipe-account-modal__btn--primary"
                onClick={submitFeedback}
                disabled={feedbackSubmitting}
              >
                {feedbackSubmitting ? tf('submitting') : tf('submit')}
              </button>
            </>
          )
        }
      >
        {feedbackSent ? (
          <p className="ipe-account-modal__message ipe-float-balls__feedback-success">
            {tf('success')}
          </p>
        ) : (
          <div className="ipe-float-balls__feedback-form">
            <p className="ipe-account-modal__message">
              {tf('feedbackIntro')}
            </p>

            <label className="ipe-float-balls__modal-field">
              <span className="ipe-float-balls__modal-label">
                {tf('feedbackEmail')}
                <span className="ipe-float-balls__modal-required" aria-hidden="true">*</span>
              </span>
              <input
                type="email"
                className="ipe-float-balls__modal-input"
                placeholder={tf('feedbackEmailPh')}
                value={feedbackEmail}
                onChange={(e) => setFeedbackEmail(e.target.value)}
                autoComplete="off"
                disabled={feedbackSubmitting}
              />
            </label>

            <label className="ipe-float-balls__modal-field">
              <span className="ipe-float-balls__modal-label">{tf('feedbackContent')}</span>
              <textarea
                className="ipe-float-balls__modal-textarea"
                placeholder={tf('feedbackContentPh')}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                disabled={feedbackSubmitting}
              />
            </label>

            <div className="ipe-float-balls__feedback-upload">
              <input
                ref={feedbackFileInputRef}
                type="file"
                accept={FEEDBACK_IMAGE_ACCEPT}
                multiple
                className="ipe-float-balls__feedback-upload-input"
                onChange={(e) => {
                  handleFeedbackImageSelect(e.target.files);
                  e.target.value = '';
                }}
                disabled={feedbackSubmitting || feedbackImages.length >= FEEDBACK_MAX_IMAGES}
                tabIndex={-1}
                aria-hidden="true"
              />
              <button
                type="button"
                className="ipe-float-balls__feedback-upload-btn"
                onClick={() => feedbackFileInputRef.current?.click()}
                disabled={feedbackSubmitting || feedbackImages.length >= FEEDBACK_MAX_IMAGES}
              >
                <ImagePlus size={16} strokeWidth={1.8} aria-hidden="true" />
                {tf('uploadImage')}
              </button>
              <span className="ipe-float-balls__feedback-upload-hint">
                {tf('uploadHint', { max: FEEDBACK_MAX_IMAGES })}
              </span>
            </div>

            {feedbackImages.length > 0 ? (
              <ul className="ipe-float-balls__feedback-thumbs" aria-label={tf('selectedImages')}>
                {feedbackImages.map((item) => (
                  <li key={item.id} className="ipe-float-balls__feedback-thumb">
                    <img src={item.previewUrl} alt="" />
                    <button
                      type="button"
                      className="ipe-float-balls__feedback-thumb-remove"
                      onClick={() => removeFeedbackImage(item.id)}
                      disabled={feedbackSubmitting}
                      aria-label={tf('removeImage')}
                    >
                      <X size={12} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {feedbackError ? (
              <p className="ipe-float-balls__feedback-error" role="alert">{feedbackError}</p>
            ) : null}
          </div>
        )}
      </SimpleModal>
    </>,
    document.body,
  );
}
